import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAppStore } from '../../stores/appStore';
import { useUIStore } from '../../stores/uiStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Shield, AlertCircle, Pencil, Trash2 } from 'lucide-react';
import { getRoles, deleteRole } from '../../services/api';
import type { Role } from '../../types';
import { toast } from '../../components/ui/toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const SYSTEM_ORG_ID = 'system';

export function RoleList() {
  const { isSuperAdmin } = useAuth();
  const { organizations, fetchOrganizations } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stateOrgId = (location.state as any)?.orgId;
  const [orgId, setOrgId] = useState<string>(
    stateOrgId || localStorage.getItem('lastSelectedRolesOrgId') || SYSTEM_ORG_ID
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Load organizations
  useEffect(() => {
    if (isSuperAdmin()) {
      fetchOrganizations().catch(() => {});
    }
  }, [isSuperAdmin, fetchOrganizations]);

  // Clear navigation state after reading it
  useEffect(() => {
    if (stateOrgId) {
      window.history.replaceState({}, '');
    }
  }, [stateOrgId]);

  // Fetch roles whenever orgId changes
  useEffect(() => {
    if (!isSuperAdmin()) return;

    const fetchRoles = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getRoles(orgId) as { roles: Role[] };
        setRoles(data.roles || []);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch roles');
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, [orgId, isSuperAdmin]);

  const handleOrgSelect = (value: string) => {
    setOrgId(value);
    localStorage.setItem('lastSelectedRolesOrgId', value);
  };

  const handleCreateClick = () => {
    navigate('/dashboard/roles/create', { state: { orgId } });
  };

  const handleEditClick = (roleId: string) => {
    navigate(`/dashboard/roles/${roleId}`, { state: { orgId } });
  };

  const handleDeleteClick = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteRole(orgId, roleToDelete.id);
      toast({
        title: 'Role Deleted',
        description: `Role "${roleToDelete.name}" has been deleted successfully.`,
      });
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      // Refresh the roles list
      const data = await getRoles(orgId) as { roles: Role[] };
      setRoles(data.roles || []);
    } catch (err: any) {
      toast({
        title: 'Delete Failed',
        description: err.message || 'Failed to delete role',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const selectedOrgName = orgId === SYSTEM_ORG_ID
    ? 'System Wide'
    : organizations.find(o => o.id === orgId)?.name ?? orgId;

  if (!isSuperAdmin()) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>Only super administrators can manage roles.</AlertDescription>
        </Alert>
        <Link to="/dashboard">
          <Button variant="outline">Back to Dashboard</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Roles</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Manage system and organization roles</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleCreateClick}>
            <Plus className="h-4 w-4 mr-2" />
            Create Role
          </Button>
          <ThemeToggleCompact />
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Organization Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Organization:</span>
            <Select value={orgId} onValueChange={handleOrgSelect}>
              <SelectTrigger className="w-[300px]">
                <SelectValue>{selectedOrgName}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SYSTEM_ORG_ID}>System Wide</SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size={48} />
        </div>
      ) : roles.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Shield className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No Roles Found</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              No roles found for {selectedOrgName}
            </p>
            <Button onClick={handleCreateClick}>Create Role</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {roles.map((role) => (
            <Card key={role.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-lg">{role.name}</CardTitle>
                    <Badge variant={role.organization_id === null ? 'default' : 'secondary'}>
                      {role.organization_id === null ? 'System' : 'Organization'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditClick(role.id)}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(role)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {role.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">{role.description}</p>
                )}
                {role.serviceid && (
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <span className="font-medium">Service ID:</span>
                    <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{role.serviceid}</code>
                  </div>
                )}
                {role.permissions && role.permissions.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Permissions:</p>
                    <div className="flex flex-wrap gap-1">
                      {role.permissions.map((perm) => (
                        <Badge key={perm} variant="outline" className="text-xs">
                          {perm}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the role "{roleToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
